<script setup>
import { ref, computed, nextTick, watch, onUnmounted, onDeactivated, onActivated } from 'vue';
import merge from 'lodash.merge';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
	GridComponent,
	SingleAxisComponent,
	ToolboxComponent,
	TooltipComponent,
	AxisPointerComponent,
	BrushComponent,
	TitleComponent,
	LegendComponent,
	DataZoomComponent
} from 'echarts/components';
import { useCommonData } from '@/hooks';
import { convertNumberToStr } from '@/utils/api';
import { echartsTooltipFormatterSingle, echartsTooltipFormatterMultiple, flatData, convertPX } from '@/utils/echarts';
import { calcNiceTicks } from '@/utils/scale';
import { ECHARTS_COLORS } from '@/utils/enum';
const { getUpperThemeColor } = useCommonData();

echarts.use([
	LineChart,
	BarChart,
	GridComponent,
	SingleAxisComponent,
	ToolboxComponent,
	TooltipComponent,
	AxisPointerComponent,
	BrushComponent,
	TitleComponent,
	LegendComponent,
	DataZoomComponent,
	CanvasRenderer
]);

const props = defineProps({
	// 是否显示tooltip的轴值，默认为false,如果为true，则tooltip的轴值会显示在tooltip的标题中,并且会覆盖props.title
	showToolTipAxisValue: {
		type: Boolean,
		default: false
	},
	title: {
		type: String,
		default: ''
	},
	xAxisData: {
		type: Array
	},
	seriesData: {
		type: Array,
		required: true,
		default: () => []
	},
	chartOptions: {
		type: Object,
		default: () => ({})
	},
	isReverse: {
		type: Boolean,
		default: false
	},
	customStyle: {
		type: String,
		default: ''
	},
	tooltipLabel: {
		type: String,
		default: ''
	},
	timeFormatter: {
		type: Function,
		default: value => value
	},
	// tooltipFormatterOrder tooltip是否排序，默认为空，不排序，如果为 desc 则降序，如果为 asc 则升序
	tooltipFormatterOrder: {
		type: String,
		default: ''
	},
	yLeftAxisLabelFormatter: {
		type: Function,
		default: value => {
			return convertNumberToStr(value);
		}
	}
});
const emits = defineEmits(['data-checked']);

const showChart = computed(() => {
	return props.seriesData.some(series => {
		return series.data?.some(ele => {
			let value;
			if (Array.isArray(ele)) {
				value = ele[1];
			} else if (typeof ele === 'object') {
				value = ele.value;
			} else {
				value = ele;
			}
			return !['', undefined, null, NaN].includes(value);
		});
	});
});
let chart = null;
const chartRef = ref(null);
watch(showChart, newVal => {
	if (!newVal) {
		clearChart();
	}
});
watch(
	() => props.seriesData,
	() => {
		if (showChart.value) {
			nextTick(() => {
				initChart();
			});
		}
	},
	{
		immediate: true,
		deep: true
	}
);
const openCustomStyle = computed(() => ['fund_holding'].includes(props.customStyle));

function initChart() {
	chart?.clear();
	chart?.dispose();
	chart = echarts.init(chartRef.value, {
		devicePixelRatio: 2
	});

	let xAxisData = props.xAxisData;
	if (!xAxisData) {
		xAxisData = props.seriesData[0].data.map(ele => {
			if (Array.isArray(ele)) {
				return ele[0];
			} else {
				return ele.name;
			}
		});
	}

	const leftData = flatData(props.seriesData.filter(ele => ele.yAxisIndex != 1));
	const rightData = flatData(props.seriesData.filter(ele => ele.yAxisIndex == 1));
	let leftTicks = {},
		rightTicks = {};
	if (rightData.length > 0) {
		leftTicks = calcNiceTicks([Math.min(...leftData, 0), Math.max(...leftData, 0)]);
		rightTicks = calcNiceTicks([Math.min(...rightData, 0), Math.max(...rightData, 0)]);
	}

	const options = {
		grid: {
			top: convertPX(16),
			left: convertPX(12),
			right: convertPX(12),
			bottom: convertPX(30),
			containLabel: true
		},
		color: ECHARTS_COLORS[getUpperThemeColor.value],
		animation: false,
		legend: {
			bottom: 0,
			padding: 0,
			itemGap: 16,
			itemWidth: convertPX(10),
			itemHeight: convertPX(10),
			inactiveColor: 'C9CDD4',
			textStyle: {
				color: '#4E5969',
				fontSize: convertPX(13),
				lineHeight: convertPX(18),
				padding: [0, 0, 0, convertPX(6)],
				height: convertPX(18)
			},
			icon: 'roundRect',
			selectedMode: false
		},
		tooltip: {
			trigger: 'axis',
			appendToBody: true,
			extraCssText: `padding: ${convertPX(8)}px ${convertPX(12)}px ${convertPX(
				12
			)}px;border: 0;border-radius: 2px;background-color: rgba(247, 248, 250, 0.90);backdrop-filter: blur(5px);`,
			axisPointer: {
				type: 'line',
				snap: true,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'dashed'
				},
				handle: {
					show: false
				}
			},
			formatter: function (params) {
				if (props.isReverse) {
					params.reverse();
				}
				let tooltipTitle = props.title;
				if (props.showToolTipAxisValue && params?.[0]?.axisValue) {
					tooltipTitle = params[0].axisValue;
				}
				if (params.length === 1) {
					const item = params[0];
					let value, unit;
					// item.data.value TODO: 不应该取item.data.value，而是取item.data
					let deepValue;
					if (openCustomStyle.value) {
						deepValue = item.data.value; // 取值结构兼容
					} else {
						deepValue = item.data;
					}

					if (Array.isArray(deepValue)) {
						value = deepValue[1];
						unit = deepValue[2];
					} else if (typeof deepValue === 'object') {
						value = deepValue.value;
						unit = deepValue.unit;
					} else {
						value = deepValue;
					}
					return echartsTooltipFormatterSingle(tooltipTitle, item.color, [
						{
							name: props.tooltipLabel || item.name,
							value: unit === '%' ? value.toFixed(2) + unit : convertNumberToStr(value) + (unit || '')
						}
					]);
				} else {
					const formatParams = params
						.map(item => {
							let value, unit;
							if (Array.isArray(item.data)) {
								value = item.data[1];
								unit = item.data[2];
							} else if (typeof item.data === 'object') {
								value = item.data.value;
								unit = item.data.unit;
							} else {
								value = item.data;
							}
							return {
								color: item.color,
								seriesName: item.seriesName,
								originValue: value,
								value:
									value != null
										? unit === '%'
											? value.toFixed(2) + unit
											: convertNumberToStr(value) + (unit || '')
										: null
							};
						})
						.filter(ele => ele.value != null);
					// 排序
					if (props.tooltipFormatterOrder == 'desc') {
						formatParams.sort((a, b) => b.originValue - a.originValue);
					} else if (props.tooltipFormatterOrder == 'asc') {
						formatParams.sort((a, b) => a.originValue - b.originValue);
					}
					return echartsTooltipFormatterMultiple(tooltipTitle, formatParams);
				}
			}
		},
		xAxis: {
			type: 'category',
			axisLine: {
				show: true,
				onZero: false,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'solid'
				}
			},
			axisTick: {
				alignWithLabel: true,
				length: 4,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'solid'
				}
			},
			axisLabel: {
				margin: convertPX(8),
				color: '#989898',
				fontSize: convertPX(12),
				align: 'center',
				lineHeight: convertPX(16),
				formatter: function (value) {
					return props.timeFormatter?.(value) || value;
				}
			},
			splitLine: {
				show: false
			},
			data: xAxisData
		},
		yAxis: [
			{
				type: 'value',
				axisLine: {
					show: false
				},
				axisTick: {
					show: false
				},
				axisLabel: {
					margin: convertPX(8),
					color: '#989898',
					fontSize: convertPX(12),
					lineHeight: convertPX(16),
					formatter: props.yLeftAxisLabelFormatter
				},
				splitLine: {
					lineStyle: {
						color: '#E5E6EB',
						width: 1,
						type: 'dashed'
					}
				},
				minInterval: 1,
				splitNumber: 5,
				...leftTicks
			},
			{
				type: 'value',
				show: !!rightData.length,
				axisLabel: {
					show: !!rightData.length,
					margin: convertPX(8),
					color: '#989898',
					fontSize: convertPX(12),
					lineHeight: convertPX(16),
					formatter: function (value) {
						return convertNumberToStr(value);
					}
				},
				axisLine: {
					show: false
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: false
				},
				splitNumber: 5,
				...rightTicks
			}
		],
		series: props.seriesData.map(series => {
			if (series.type == 'bar') {
				return merge(
					{
						barMaxWidth: convertPX(16),
						barCategoryGap: 4,
						large: true
					},
					preformatSeries(series)
				);
			} else if (series.type == 'line') {
				return merge(
					{
						type: 'line',
						symbol: 'emptyCircle',
						symbolSize: 6,
						showSymbol: true,
						lineStyle: {
							width: series.width || 1
						}
					},
					series
				);
			}
		})
	};
	chart.setOption(merge(options, props.chartOptions));

	const _series = options.series;
	lastActiveUpdate(_series);

	// zrender 事件与 echarts 事件不同。前者是当鼠标在任何地方都会被触发，而后者是只有当鼠标在图形元素上时才能被触发。
	chart.getZr().on('click', event => {
		const pos = [event.offsetX, event.offsetY];
		// 检查点击位置是否在图表的网格区域内
		if (chart.containPixel('grid', pos)) {
			// 将像素坐标转换为数据索引
			let [dataIndex] = chart.convertFromPixel({ seriesIndex: 0 }, pos);
			const newSeries = onChartChecked(dataIndex, _series);
			// 重新更新图表数据
			newSeries &&
				chart.setOption({
					series: newSeries
				});
		}
	});
}

const activeIndex = ref(-1);
const lastActiveUpdate = series => {
	if (!openCustomStyle.value) return;
	if (series?.[0].data.length) {
		activeIndex.value = series[0].data.length - 1;
	}
};
const preformatSeries = seriesItem => {
	if (!openCustomStyle.value) return seriesItem;
	const { data, ...rest } = seriesItem;
	return {
		...rest,
		data: data.map((val, index) => {
			return {
				value: val,
				itemStyle: {
					// 默认选中最后（最新）一根柱子
					opacity: index === data.length - 1 ? 1 : 0.4
				}
			};
		})
	};
};
const onChartChecked = (idx, _series) => {
	if (!openCustomStyle.value) return;
	if (typeof idx !== 'number') return;

	const dataIndex = Math.abs(idx);
	const sameClickedIndex = activeIndex.value === dataIndex;
	const clickedIndex = dataIndex;
	activeIndex.value = clickedIndex;

	let pickValue;
	const newSeries = _series.map(series => {
		const { data, ...rest } = series;
		return {
			...rest,
			data: data.map((item, index) => {
				const deepValue = item.value || item;
				if (index === clickedIndex) pickValue = deepValue;
				return {
					value: deepValue,
					itemStyle: {
						// 更新柱状图的透明度，以突出高亮选中
						opacity: index === clickedIndex ? 1 : 0.4
					}
				};
			})
		};
	});

	if (sameClickedIndex) {
		// emits('data-checked', { index: null, data: null });
		// console.log('-------- 反选');
		// activeIndex.value = -1; // reset
	} else {
		emits('data-checked', { index: clickedIndex, data: pickValue[0] });
		// console.log('-------- 正选', clickedIndex, pickValue);
	}

	return newSeries;
};

function clearChart() {
	chart?.dispatchAction({
		type: 'hideTip'
	});
	chart?.clear();
	chart?.dispose();
	chart = null;
}

onActivated(() => {
	if (showChart.value) {
		nextTick(() => {
			initChart();
		});
	}
});
onUnmounted(() => {
	clearChart();
});
onDeactivated(() => {
	clearChart();
});
</script>

<template>
	<div class="line-bar-chart" v-loading="!seriesData.length">
		<div v-if="showChart" class="chart-main">
			<div class="chart-canvas" ref="chartRef"></div>
		</div>
		<Empty v-else-if="seriesData.length && !showChart" class="chart-empty"></Empty>
	</div>
</template>

<style lang="less" scoped>
.line-bar-chart,
.chart-empty {
	width: 100%;
	height: 100%;
	.chart-main {
		height: 100%;
		display: flex;
		flex-direction: column;

		.chart-canvas {
			flex: 1;
			min-height: 0;
		}
	}
}
</style>
<style lang="less">
.echarts-tooltip {
	.echarts-tooltip-name {
		display: flex;
		align-items: center;

		font-family: 'Barlow';

		font-weight: 400;
		font-size: 12px;
		line-height: 20px;
		color: #1d2129;
	}

	.echarts-tooltip-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-column-gap: 4px;
	}

	.echarts-tooltip-wrapper {
		margin-top: 4px;
		min-width: 100px;
		padding: 4px 6px;
		background-color: #ffffff;

		display: flex;
		align-items: center;
		justify-content: space-between;

		font-weight: 400;
		font-size: 12px;
	}

	.echarts-tooltip-item-name {
		display: flex;
		align-items: center;

		color: #4e5969;
	}

	.echarts-tooltip-item-value {
		margin-left: 16px;

		font-family: 'Barlow';
		color: #1d2129;
	}
}
</style>
